import { HoverCard } from "@kobalte/core";
import { A } from "@solidjs/router";
import { TbChevronDown } from "solid-icons/tb";
import { type Component, createSignal, type JSXElement, Show } from "solid-js";
import { UserAvatarWithMenu } from "@core/components/_original/UserAvatarWithMenu";
import { StIcon } from "@core/components/icons";
import { urls } from "@core/lib/urls";
import { useWire } from "@core/wire";
import styles from "./LandingPageNavbar.module.css";
import { classNames } from "@core/lib/classNames";
import { StorytellLogo } from "@core/components/StorytellLogo";
import { BurgerButton } from "@core/components/BurgerButton";
import { createBreakpoints } from "@solid-primitives/media";
import { LandingPageNavbarBanner } from "./LandingPageNavbarBanner";

const HoverLink = (props: { href: string; icon: JSXElement; label: string }) => (
  <a class={styles["header-hover-card-content-column-link"]} href={props.href}>
    <span class={styles["header-hover-card-content-column-link-icon"]}>{props.icon}</span>

    <span>{props.label}</span>
  </a>
);

const PlatformHoverCard: Component<{ mount?: HTMLElement }> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  return (
    <HoverCard.Root
      slide
      placement="bottom-start"
      openDelay={100}
      closeDelay={0}
      onOpenChange={setIsOpen}
      open={isOpen()}
    >
      <HoverCard.Trigger as="button" on:click={() => setIsOpen((s) => !s)}>
        <span class={styles["header-nav-link"]}>
          <span>Platform</span>
          <span class={styles["header-nav-link-icon"]}>
            <StIcon icon={TbChevronDown} size={20} />
          </span>
          <div class={styles["header-nav-link-underline"]} />
        </span>
      </HoverCard.Trigger>
      <HoverCard.Portal mount={props.mount}>
        <HoverCard.Content class={classNames("dark", styles["header-hover-card"])}>
          <div class={styles["header-hover-card-content"]}>
            <div class={styles["header-hover-card-content-column"]}>
              <h4 class={classNames(styles["header-hover-card-content-column-title"], styles["text-gradient"])}>
                Insights
              </h4>

              <HoverLink
                href="https://web.storytell.ai/every-file-meaningful-insights"
                icon={icons.everyFile}
                label="Turn Every File into Meaningful Insights"
              />
              <HoverLink
                href="https://web.storytell.ai/dynamic-insights"
                icon={icons.dynamicInsights}
                label="Dynamic Insights™"
              />
              <HoverLink
                href="https://web.storytell.ai/gain-contextual-intelligence"
                icon={icons.contextualIntelligence}
                label="Gain Contextual Intelligence"
              />
              <HoverLink
                href="https://web.storytell.ai/trust-but-verify"
                icon={icons.trustButVerify}
                label="Trust but Verify"
              />
            </div>
            <div class={styles["header-hover-card-content-column"]}>
              <h4 class={classNames(styles["header-hover-card-content-column-title"], styles["text-gradient"])}>
                Productivity
              </h4>

              <HoverLink
                href="https://web.storytell.ai/become-a-master-prompter"
                icon={icons.masterPrompter}
                label="Become a Master Prompter"
              />
              <HoverLink
                href="https://web.storytell.ai/automate-tasks"
                icon={icons.automateTasks}
                label="Automate Tasks"
              />
              <HoverLink href="https://web.storytell.ai/llm-router" icon={icons.llmRouter} label="LLM Router" />
            </div>
            <div class={styles["header-hover-card-content-column"]}>
              <h4 class={classNames(styles["header-hover-card-content-column-title"], styles["text-gradient"])}>
                Collaboration
              </h4>

              <HoverLink
                href="https://web.storytell.ai/create-team-project-spaces"
                icon={icons.collections}
                label="Collections"
              />
              <HoverLink href="https://web.storytell.ai/de-silo-teams" icon={icons.deSiloTeams} label="De-silo Teams" />
            </div>
            <div class={styles["header-hover-card-content-column"]}>
              <h4 class={classNames(styles["header-hover-card-content-column-title"], styles["text-gradient"])}>
                Security & Control
              </h4>

              <HoverLink
                href="https://web.storytell.ai/trust-and-security"
                icon={icons.trustAndSecurity}
                label="Trust and Security"
              />
              <HoverLink href="https://web.storytell.ai/ai-firewall" icon={icons.aiFirewall} label="AI Firewall" />

              <HoverLink
                href="https://web.storytell.ai/user-role-based-access-controls"
                icon={icons.rbac}
                label="Role-Based Access Controls"
              />

              <HoverLink
                href="https://web.storytell.ai/manage-costs"
                icon={icons.manageCosts}
                label="Cost Management"
              />
            </div>
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
};

const UseCasesHoverCard: Component<{ mount?: HTMLElement }> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  return (
    <HoverCard.Root
      slide
      placement="bottom-start"
      openDelay={100}
      closeDelay={0}
      onOpenChange={setIsOpen}
      open={isOpen()}
    >
      <HoverCard.Trigger as="button" on:click={() => setIsOpen((s) => !s)}>
        <span class={styles["header-nav-link"]}>
          <span>Use Cases</span>
          <span class={styles["header-nav-link-icon"]}>
            <StIcon icon={TbChevronDown} size={20} />
          </span>
          <div class={styles["header-nav-link-underline"]} />
        </span>
      </HoverCard.Trigger>
      <HoverCard.Portal mount={props.mount}>
        <HoverCard.Content class={classNames("dark", styles["header-hover-card"])}>
          <div class="grid grid-cols-[1fr_1.2fr_1fr] gap-x-10 gap-y-12 w-[43rem]">
            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Product</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/create-product-requirement-docs"
              >
                Create Product Requirement Docs
              </a>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/sprint-planning"
              >
                Sprint Planning
              </a>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/prioritization-roadmap-insights"
              >
                Prioritization & Roadmap Insights
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Venture Capital</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/financial-risk-valuation-analysis"
              >
                Financial Risk & Valuation Analysis
              </a>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/company-deep-dives"
              >
                Company Deep Dives
              </a>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/analyze-startup-pitch-deck"
              >
                Pitch Deck Analysis
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Analyst</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/investor-updates"
              >
                Investor Updates
              </a>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/market-landscape-analysis"
              >
                Market Landscape Analysis
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Finance</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/revenue-forecast-reports"
              >
                Revenue Forecast Reports
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Sales</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/personalized-sales-pitch"
              >
                Personalized Sales Pitch
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Marketing</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/sales-battlecards"
              >
                Sales Battlecards
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Customer Success</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/churn-risk-prediction-retention-strategy"
              >
                Churn Risk Prediction & Retention Strategy
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Team Dynamics</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/master-interpersonal-interactions"
              >
                Improve team communications
              </a>
            </div>

            <div class="grid gap-y-3 auto-rows-min grid-cols-1">
              <h4 class={classNames("text-sm", styles["text-gradient"])}>Security & Compliance</h4>
              <a
                class="text-base leading-perfect-fifth text-white hover:text-pink-300 transition-colors"
                href="https://web.storytell.ai/use-case/security-data-into-documentation"
              >
                Create security documentation
              </a>
            </div>
          </div>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
};

const ResourcesHoverCard: Component<{ mount?: HTMLElement }> = (props) => {
  const [isOpen, setIsOpen] = createSignal(false);
  return (
    <HoverCard.Root placement="bottom-start" openDelay={100} closeDelay={0} open={isOpen()} onOpenChange={setIsOpen}>
      <HoverCard.Trigger as="span">
        <a href="https://web.storytell.ai/" class={styles["header-nav-link"]}>
          <span>Resources</span>
          <span
            class={styles["header-nav-link-icon"]}
            on:click={(e) => {
              e.stopImmediatePropagation();
              e.preventDefault();
              setIsOpen((s) => !s);
            }}
          >
            <StIcon icon={TbChevronDown} size={20} />
          </span>
          <div class={styles["header-nav-link-underline"]} />
        </a>
      </HoverCard.Trigger>
      <HoverCard.Portal mount={props.mount}>
        <HoverCard.Content class={classNames("dark", styles["header-hover-card-resources"])}>
          <a href="https://docs.storytell.ai/introduction" class={styles["header-hover-card-content-column-link"]}>
            <span>Documentation</span>
          </a>
          <a href="https://web.storytell.ai/blog" class={styles["header-hover-card-content-column-link"]}>
            <span>Blog</span>
          </a>
          <a href="https://web.storytell.ai/webinars" class={styles["header-hover-card-content-column-link"]}>
            <span>Webinars & Workshops</span>
          </a>
          <a href="https://web.storytell.ai/prompts" class={styles["header-hover-card-content-column-link"]}>
            <span>AI Prompt Library</span>
          </a>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
};

const NavbarMobile = (props: { dark?: boolean; static?: boolean }) => {
  const [open, setOpen] = createSignal(false);
  const wire = useWire();
  let ref!: HTMLHeadElement;
  const [landingPageBanner, setLandingPageBanner] = createSignal(true);

  const bg = () => (props.dark ? "bg-indigo-1000" : "bg-white");

  return (
    <header
      class={classNames(
        props.dark && "dark",
        styles.header,
        open() && `fixed inset-0 ${bg()}`,
        props.static && !open() && styles["header--static"],
      )}
      ref={ref}
    >
      <Show
        when={open()}
        fallback={
          <>
            <LandingPageNavbarBanner show={landingPageBanner()} onClose={() => setLandingPageBanner(false)} />
            <nav class={styles["header-nav"]}>
              <div class={styles["header-nav-container"]}>
                <StorytellLogo />

                <div class={styles["mobile-header__hamburger"]}>
                  <BurgerButton controls="main-navigation" open={open()} setOpen={setOpen} />
                </div>
              </div>
            </nav>

            <div class={styles["header-nav-underline"]} />
          </>
        }
      >
        <nav class={styles["header-nav"]}>
          <div class="relative">
            <div class={styles["header-nav-container"]}>
              <StorytellLogo />

              <div class={styles["mobile-header__hamburger"]}>
                <BurgerButton controls="main-navigation" open={open()} setOpen={setOpen} />
              </div>
            </div>
            <div class={styles["header-nav-underline"]} />
          </div>

          <ul class={styles["header-nav-links"]}>
            <div class={styles["header-nav-links-left"]}>
              <li>
                <PlatformHoverCard mount={ref} />
              </li>
              <li>
                <UseCasesHoverCard mount={ref} />
              </li>
              <li>
                <ResourcesHoverCard mount={ref} />
              </li>
              <li>
                <a href="https://web.storytell.ai/why-storytell-enterprise-ai" class={styles["header-nav-link"]}>
                  <span>Why Storytell</span>
                  <div class={styles["header-nav-link-underline"]} />
                </a>
              </li>
              <li>
                <a href="https://web.storytell.ai/book-a-demo" class={styles["header-nav-link"]}>
                  <span>Book an Enterprise Demo</span>
                  <div class={styles["header-nav-link-underline"]} />
                </a>
              </li>
            </div>

            <div class={styles["header-nav-links-right"]}>
              <Show
                when={wire.services.auth.isVerified()}
                fallback={
                  <>
                    <li>
                      <A href="/auth/login" class={styles["header-nav-auth-login"]}>
                        Login
                      </A>
                    </li>
                    <li>
                      <A href="/auth/signup" class={styles["header-nav-auth-signup"]}>
                        Signup
                      </A>
                    </li>
                  </>
                }
              >
                <Show when={wire.services.collections.getRootsLoaded()}>
                  <A
                    href={urls.collection(wire.services.collections.getPersonalRoot()?.id ?? "")}
                    class="text-sm font-semibold leading-6 text-gray-100 underline underline-offset-2 mr-2"
                  >
                    Dashboard
                  </A>
                </Show>
                <div class="inline-block w-auto h-auto">
                  <UserAvatarWithMenu />
                </div>
              </Show>
            </div>
          </ul>
        </nav>
      </Show>
    </header>
  );
};

export const LandingPageNavbar = (props: { dark?: boolean; static?: boolean }) => {
  const wire = useWire();

  const [landingPageBanner, setLandingPageBanner] = createSignal(true);
  const breakpoints = createBreakpoints(
    {
      desktop: "994px",
    },
    {
      fallbackState: {
        desktop: true,
        key: "desktop",
      },
    },
  );
  return (
    <Show when={breakpoints.desktop} fallback={<NavbarMobile {...props} />}>
      <header class={classNames(props.dark && "dark", styles.header, props.static && styles["header--static"])}>
        <LandingPageNavbarBanner show={landingPageBanner()} onClose={() => setLandingPageBanner(false)} />
        <nav class={styles["header-nav"]}>
          <div class={styles["header-nav-container"]}>
            <StorytellLogo />
            <ul class={styles["header-nav-links"]}>
              <div class={styles["header-nav-links-left"]}>
                <li>
                  <PlatformHoverCard />
                </li>
                <li>
                  <UseCasesHoverCard />
                </li>
                <li>
                  <ResourcesHoverCard />
                </li>
                <li>
                  <a href="https://web.storytell.ai/why-storytell-enterprise-ai" class={styles["header-nav-link"]}>
                    <span>Why Storytell</span>
                    <div class={styles["header-nav-link-underline"]} />
                  </a>
                </li>
                <li>
                  <a href="https://web.storytell.ai/book-a-demo" class={styles["header-nav-link"]}>
                    <span>Book an Enterprise Demo</span>
                    <div class={styles["header-nav-link-underline"]} />
                  </a>
                </li>
              </div>

              <div class={styles["header-nav-links-right"]}>
                <Show
                  when={wire.services.auth.isVerified()}
                  fallback={
                    <>
                      <li>
                        <A href="/auth/login" class={styles["header-nav-auth-login"]}>
                          Login
                        </A>
                      </li>
                      <li>
                        <A href="/auth/signup" class={styles["header-nav-auth-signup"]}>
                          Signup
                        </A>
                      </li>
                    </>
                  }
                >
                  <Show when={wire.services.collections.getRootsLoaded()}>
                    <A
                      href={urls.collection(wire.services.collections.getPersonalRoot()?.id ?? "")}
                      class="text-sm font-semibold leading-6 text-gray-100 underline underline-offset-2 mr-2"
                    >
                      Dashboard
                    </A>
                  </Show>
                  <div class="inline-block w-auto h-auto">
                    <UserAvatarWithMenu />
                  </div>
                </Show>
              </div>
            </ul>

            <div class={styles["header-nav-underline"]} />
          </div>
        </nav>
      </header>
    </Show>
  );
};

/**
 * Not using the usual solid-icons since these are supposed to match the webflow ones perfectly
 */
const icons = {
  everyFile: (
    <svg width="100%" height="100%" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.26135 6.04969L6.84213 5.15906L5.95151 2.73797C5.89876 2.59469 5.80334 2.47103 5.67811 2.38368C5.55288 2.29633 5.40388 2.2495 5.25119 2.2495C5.09851 2.2495 4.9495 2.29633 4.82427 2.38368C4.69905 2.47103 4.60362 2.59469 4.55088 2.73797L3.65932 5.15625L1.23822 6.04688C1.09494 6.09962 0.971283 6.19504 0.883936 6.32027C0.796589 6.4455 0.749756 6.59451 0.749756 6.74719C0.749756 6.89987 0.796589 7.04888 0.883936 7.17411C0.971283 7.29934 1.09494 7.39476 1.23822 7.4475L3.65604 8.34375L4.54666 10.7634C4.5994 10.9067 4.69483 11.0304 4.82006 11.1177C4.94528 11.2051 5.09429 11.2519 5.24697 11.2519C5.39966 11.2519 5.54866 11.2051 5.67389 11.1177C5.79912 11.0304 5.89454 10.9067 5.94729 10.7634L6.83791 8.34422L9.25901 7.45359C9.40229 7.40085 9.52595 7.30543 9.61329 7.1802C9.70064 7.05497 9.74747 6.90596 9.74747 6.75328C9.74747 6.6006 9.70064 6.45159 9.61329 6.32636C9.52595 6.20113 9.40229 6.10571 9.25901 6.05297L9.26135 6.04969ZM6.58057 7.64063C6.47923 7.67782 6.3872 7.73662 6.31087 7.81296C6.23454 7.88929 6.17573 7.98132 6.13854 8.08266L5.24791 10.4948L4.35916 8.08078C4.32194 7.97998 4.26332 7.88844 4.18734 7.81245C4.11135 7.73647 4.01981 7.67785 3.91901 7.64063L1.50682 6.75L3.91901 5.85938C4.01981 5.82215 4.11135 5.76353 4.18734 5.68755C4.26332 5.61157 4.32194 5.52002 4.35916 5.41922L5.24979 3.00703L6.14041 5.41922C6.17761 5.52056 6.23641 5.61259 6.31274 5.68892C6.38907 5.76525 6.4811 5.82406 6.58244 5.86125L8.99463 6.75188L6.58057 7.64063ZM6.74979 1.875C6.74979 1.77554 6.7893 1.68016 6.85962 1.60984C6.92995 1.53951 7.02533 1.5 7.12479 1.5H7.87479V0.75C7.87479 0.650544 7.9143 0.555161 7.98462 0.484835C8.05495 0.414509 8.15033 0.375 8.24979 0.375C8.34924 0.375 8.44463 0.414509 8.51495 0.484835C8.58528 0.555161 8.62479 0.650544 8.62479 0.75V1.5H9.37479C9.47424 1.5 9.56963 1.53951 9.63995 1.60984C9.71028 1.68016 9.74979 1.77554 9.74979 1.875C9.74979 1.97446 9.71028 2.06984 9.63995 2.14017C9.56963 2.21049 9.47424 2.25 9.37479 2.25H8.62479V3C8.62479 3.09946 8.58528 3.19484 8.51495 3.26517C8.44463 3.33549 8.34924 3.375 8.24979 3.375C8.15033 3.375 8.05495 3.33549 7.98462 3.26517C7.9143 3.19484 7.87479 3.09946 7.87479 3V2.25H7.12479C7.02533 2.25 6.92995 2.21049 6.85962 2.14017C6.7893 2.06984 6.74979 1.97446 6.74979 1.875ZM11.6248 4.125C11.6248 4.22446 11.5853 4.31984 11.515 4.39017C11.4446 4.46049 11.3492 4.5 11.2498 4.5H10.8748V4.875C10.8748 4.97446 10.8353 5.06984 10.765 5.14017C10.6946 5.21049 10.5992 5.25 10.4998 5.25C10.4003 5.25 10.3049 5.21049 10.2346 5.14017C10.1643 5.06984 10.1248 4.97446 10.1248 4.875V4.5H9.74979C9.65033 4.5 9.55495 4.46049 9.48462 4.39017C9.4143 4.31984 9.37479 4.22446 9.37479 4.125C9.37479 4.02554 9.4143 3.93016 9.48462 3.85984C9.55495 3.78951 9.65033 3.75 9.74979 3.75H10.1248V3.375C10.1248 3.27554 10.1643 3.18016 10.2346 3.10984C10.3049 3.03951 10.4003 3 10.4998 3C10.5992 3 10.6946 3.03951 10.765 3.10984C10.8353 3.18016 10.8748 3.27554 10.8748 3.375V3.75H11.2498C11.3492 3.75 11.4446 3.78951 11.515 3.85984C11.5853 3.93016 11.6248 4.02554 11.6248 4.125Z"
        fill="currentColor"
      />
    </svg>
  ),
  dynamicInsights: (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 21C3.45 21 2.97933 20.8043 2.588 20.413C2.19667 20.0217 2.00067 19.5507 2 19V12H4V19H13V21H4ZM8 17C7.45 17 6.97933 16.8043 6.588 16.413C6.19667 16.0217 6.00067 15.5507 6 15V8H8V15H17V17H8ZM12 13C11.45 13 10.9793 12.8043 10.588 12.413C10.1967 12.0217 10.0007 11.5507 10 11V5C10 4.45 10.196 3.97933 10.588 3.588C10.98 3.19667 11.4507 3.00067 12 3H20C20.55 3 21.021 3.196 21.413 3.588C21.805 3.98 22.0007 4.45067 22 5V11C22 11.55 21.8043 12.021 21.413 12.413C21.0217 12.805 20.5507 13.0007 20 13H12ZM12 11H20V7H12V11Z"
        fill="currentColor"
      />
    </svg>
  ),
  contextualIntelligence: (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M20 20H4C3.45 20 2.97933 19.8043 2.588 19.413C2.19667 19.0217 2.00067 18.5507 2 18V6C2 5.45 2.196 4.97933 2.588 4.588C2.98 4.19667 3.45067 4.00067 4 4H14V6H4V18H20V11H22V18C22 18.55 21.8043 19.021 21.413 19.413C21.0217 19.805 20.5507 20.0007 20 20ZM6 16H13V13H6V16ZM6 11H13V8H6V11ZM15 16H18V11H15V16ZM18 9V7H16V5H18V3H20V5H22V7H20V9H18Z"
        fill="currentColor"
      />
    </svg>
  ),
  trustButVerify: (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10.5 2C11.08 2 11.65 2.058 12.199 2.17C12.4589 2.22278 12.6872 2.37664 12.8337 2.59775C12.9802 2.81886 13.0328 3.08909 12.98 3.349C12.9272 3.60891 12.7734 3.83721 12.5523 3.98368C12.3312 4.13014 12.0609 4.18278 11.801 4.13C10.5415 3.87306 9.23408 3.99534 8.04406 4.48139C6.85404 4.96743 5.83486 5.7954 5.11536 6.86063C4.39586 7.92586 4.00837 9.1805 4.00186 10.4659C3.99536 11.7514 4.37014 13.0099 5.07881 14.0823C5.78749 15.1548 6.79825 15.993 7.98329 16.4911C9.16833 16.9892 10.4744 17.1247 11.7365 16.8805C12.9985 16.6363 14.1598 16.0234 15.0736 15.1193C15.9873 14.2152 16.6125 13.0604 16.87 11.801C16.8961 11.6723 16.9474 11.55 17.0208 11.4411C17.0942 11.3322 17.1883 11.2388 17.2978 11.1663C17.4072 11.0938 17.5299 11.0435 17.6588 11.0184C17.7877 10.9933 17.9203 10.9939 18.049 11.02C18.1777 11.0461 18.3 11.0974 18.4089 11.1708C18.5178 11.2441 18.6112 11.3383 18.6837 11.4477C18.7562 11.5572 18.8065 11.6799 18.8316 11.8088C18.8567 11.9377 18.8561 12.0703 18.83 12.199C18.5864 13.3916 18.0896 14.518 17.373 15.502L17.176 15.762L20.828 19.414C21.0093 19.5935 21.115 19.8356 21.1237 20.0905C21.1323 20.3455 21.0432 20.5942 20.8745 20.7856C20.7059 20.977 20.4704 21.0967 20.2164 21.1202C19.9623 21.1437 19.7089 21.0692 19.508 20.912L19.414 20.828L15.762 17.176C14.6975 18.015 13.4477 18.5872 12.117 18.8449C10.7864 19.1026 9.41333 19.0384 8.11252 18.6576C6.81171 18.2768 5.62084 17.5904 4.63925 16.6557C3.65766 15.7211 2.91382 14.5652 2.46979 13.2846C2.02576 12.004 1.89442 10.6357 2.08672 9.29404C2.27902 7.95235 2.78939 6.67609 3.57525 5.57175C4.3611 4.46742 5.39966 3.56702 6.60427 2.94569C7.80887 2.32436 9.1446 2.00012 10.5 2ZM19 1C19.1871 1 19.3704 1.05248 19.5292 1.15147C19.6879 1.25046 19.8157 1.392 19.898 1.56L19.946 1.677L20.076 2.055C20.2132 2.45718 20.4343 2.82563 20.7246 3.13594C21.0149 3.44625 21.3678 3.69135 21.76 3.855L21.945 3.925L22.323 4.054C22.5102 4.11786 22.6742 4.2358 22.7944 4.3929C22.9146 4.54999 22.9855 4.7392 22.9981 4.93658C23.0107 5.13396 22.9645 5.33065 22.8654 5.50178C22.7662 5.67291 22.6185 5.8108 22.441 5.898L22.323 5.946L21.945 6.076C21.5428 6.2132 21.1744 6.43428 20.8641 6.72459C20.5538 7.01491 20.3087 7.36783 20.145 7.76L20.075 7.945L19.946 8.323C19.882 8.51014 19.764 8.6741 19.6069 8.79416C19.4497 8.91423 19.2605 8.98499 19.0631 8.99752C18.8657 9.01004 18.6691 8.96376 18.498 8.86452C18.3269 8.76528 18.1891 8.61755 18.102 8.44L18.054 8.323L17.924 7.945C17.7868 7.54282 17.5657 7.17437 17.2754 6.86406C16.9851 6.55375 16.6322 6.30865 16.24 6.145L16.055 6.075L15.677 5.946C15.4898 5.88214 15.3258 5.7642 15.2056 5.6071C15.0854 5.45001 15.0146 5.2608 15.0019 5.06342C14.9893 4.86604 15.0355 4.66935 15.1346 4.49822C15.2338 4.32709 15.3815 4.1892 15.559 4.102L15.677 4.054L16.055 3.924C16.4572 3.7868 16.8256 3.56572 17.136 3.27541C17.4463 2.98509 17.6914 2.63217 17.855 2.24L17.925 2.055L18.054 1.677C18.1214 1.47959 18.2488 1.30818 18.4184 1.18679C18.5881 1.06539 18.7914 1.00008 19 1ZM19 4.196C18.7635 4.4937 18.4937 4.76344 18.196 5C18.4947 5.23667 18.7627 5.50467 19 5.804C19.2367 5.50533 19.5047 5.23733 19.804 5C19.5063 4.76344 19.2366 4.4937 19 4.196Z"
        fill="currentColor"
      />
    </svg>
  ),
  masterPrompter: (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd" d="M9 12V20H7V12H9Z" fill="currentColor" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M17 12V20H15V12H17Z" fill="currentColor" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M9 4V8H7V4H9Z" fill="currentColor" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M17 4V10H15V4H17Z" fill="currentColor" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M5 8H11V10H5V8Z" fill="currentColor" />
      <path fill-rule="evenodd" clip-rule="evenodd" d="M13 12H19V14H13V12Z" fill="currentColor" />
    </svg>
  ),
  automateTasks: (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M8.7587 4H15.2413C16.0463 3.99999 16.7106 3.99998 17.2518 4.04419C17.8139 4.09012 18.3306 4.18868 18.816 4.43597C19.5686 4.81947 20.1805 5.43139 20.564 6.18404C20.8113 6.66937 20.9099 7.18608 20.9558 7.74818C21 8.28936 21 8.95372 21 9.75868V13.2408C21 14.0457 21 14.7101 20.9558 15.2513C20.9099 15.8134 20.8113 16.3301 20.564 16.8154C20.1805 17.568 19.5686 18.18 18.816 18.5635C18.3306 18.8108 17.8139 18.9093 17.2518 18.9552C16.7106 18.9995 16.0463 18.9995 15.2413 18.9994H15.0314L12 22.5368L8.96859 18.9994H8.75872C7.95374 18.9995 7.28938 18.9995 6.74817 18.9552C6.18608 18.9093 5.66937 18.8108 5.18404 18.5635C4.43139 18.18 3.81947 17.568 3.43597 16.8154C3.18868 16.3301 3.09012 15.8134 3.04419 15.2513C2.99998 14.7101 2.99999 14.0457 3 13.2407V9.7587C2.99999 8.95373 2.99998 8.28937 3.04419 7.74818C3.09012 7.18608 3.18868 6.66937 3.43597 6.18404C3.81947 5.43139 4.43139 4.81947 5.18404 4.43597C5.66937 4.18868 6.18608 4.09012 6.74817 4.04419C7.28937 3.99998 7.95373 3.99999 8.7587 4ZM6.91104 6.03755C6.47262 6.07337 6.24842 6.1383 6.09202 6.21799C5.7157 6.40973 5.40973 6.7157 5.21799 7.09202C5.1383 7.24842 5.07337 7.47262 5.03755 7.91104C5.00078 8.36113 5 8.94342 5 9.8V13.1994C5 14.056 5.00078 14.6383 5.03755 15.0884C5.07337 15.5268 5.1383 15.751 5.21799 15.9074C5.40973 16.2837 5.7157 16.5897 6.09202 16.7815C6.24842 16.8611 6.47262 16.9261 6.91104 16.9619C7.36113 16.9987 7.94342 16.9994 8.8 16.9994H9.88858L12 19.4632L14.1115 16.9994H15.2C16.0566 16.9994 16.6389 16.9987 17.089 16.9619C17.5274 16.9261 17.7516 16.8611 17.908 16.7815C18.2843 16.5897 18.5903 16.2837 18.782 15.9074C18.8617 15.751 18.9266 15.5268 18.9624 15.0884C18.9992 14.6383 19 14.056 19 13.1994V9.8C19 8.94342 18.9992 8.36113 18.9624 7.91104C18.9266 7.47262 18.8617 7.24842 18.782 7.09202C18.5903 6.7157 18.2843 6.40973 17.908 6.21799C17.7516 6.1383 17.5274 6.07337 17.089 6.03755C16.6389 6.00078 16.0566 6 15.2 6H8.8C7.94342 6 7.36113 6.00078 6.91104 6.03755ZM9.00002 7.58578L12.4142 11L9.00002 14.4142L7.5858 13L9.5858 11L7.5858 8.99999L9.00002 7.58578ZM12 13H16V15H12V13Z"
        fill="currentColor"
      />
    </svg>
  ),
  llmRouter: (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M12.3689 4.3883C12.1252 4.34257 11.8751 4.34257 11.6313 4.3883C11.3885 4.43386 11.1397 4.54817 10.3007 4.96764L7.10072 6.56764C6.0727 7.08165 5.77787 7.24366 5.5713 7.45457C5.36386 7.66636 5.20611 7.92161 5.10944 8.20186C5.01318 8.48094 5.00013 8.8171 5.00013 9.96646V13.3395C5.00013 14.3752 5.01096 14.6777 5.09164 14.9352C5.17262 15.1936 5.30526 15.433 5.48151 15.6386C5.65707 15.8435 5.90785 16.013 6.78614 16.5619L9.98614 18.5619C10.9713 19.1776 11.2665 19.3464 11.557 19.4124C11.8487 19.4787 12.1516 19.4787 12.4433 19.4124C12.7338 19.3464 13.029 19.1776 14.0141 18.5619L17.2141 16.5619C18.0924 16.013 18.3432 15.8435 18.5188 15.6386C18.695 15.433 18.8276 15.1936 18.9086 14.9352C18.9893 14.6777 19.0001 14.3752 19.0001 13.3395V9.96646C19.0001 8.8171 18.9871 8.48094 18.8908 8.20186C18.7942 7.92161 18.6364 7.66636 18.429 7.45457C18.2224 7.24366 17.9276 7.08166 16.8995 6.56764L13.6995 4.96764C12.8606 4.54817 12.6118 4.43386 12.3689 4.3883ZM11.2625 2.4226C11.75 2.33114 12.2502 2.33114 12.7377 2.4226C13.291 2.52639 13.8041 2.78334 14.4839 3.12368C14.5201 3.14182 14.5568 3.16019 14.594 3.17879L17.794 4.77879C17.84 4.80182 17.8855 4.82452 17.9304 4.84691C18.7604 5.26123 19.3862 5.57358 19.8578 6.05513C20.2727 6.47871 20.5882 6.98922 20.7815 7.54972C21.0013 8.18692 21.0008 8.88629 21.0002 9.81399C21.0002 9.86414 21.0001 9.91496 21.0001 9.96646V13.3395C21.0001 13.3857 21.0002 13.4312 21.0002 13.4761C21.0007 14.314 21.0011 14.9461 20.8171 15.5332C20.6552 16.0501 20.3899 16.5287 20.0374 16.9401C19.637 17.4072 19.1008 17.7419 18.39 18.1856C18.3519 18.2093 18.3133 18.2334 18.2741 18.2579L15.0741 20.2579C15.03 20.2855 14.9864 20.3128 14.9434 20.3397C14.1488 20.8371 13.5498 21.212 12.8865 21.3627C12.303 21.4953 11.6973 21.4953 11.1138 21.3627C10.4504 21.212 9.85146 20.8371 9.05687 20.3397C9.01388 20.3128 8.97032 20.2855 8.92614 20.2579L5.72614 18.2579C5.68701 18.2334 5.64839 18.2093 5.61027 18.1856C4.89951 17.7419 4.36323 17.4072 3.96288 16.9401C3.61039 16.5287 3.34511 16.0501 3.18314 15.5332C2.99917 14.9461 2.99956 14.314 3.00008 13.4761C3.00011 13.4312 3.00013 13.3857 3.00013 13.3395V9.96646C3.00013 9.91496 3.0001 9.86414 3.00007 9.814C2.99944 8.8863 2.99897 8.18692 3.21875 7.54972C3.41208 6.98922 3.72759 6.47871 4.14246 6.05513C4.6141 5.57358 5.23985 5.26123 6.06989 4.84691C6.11476 4.82452 6.16022 4.80182 6.20629 4.77879L9.4063 3.17879C9.44349 3.16019 9.48019 3.14181 9.51641 3.12368C10.1961 2.78334 10.7093 2.52639 11.2625 2.4226Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M4.44735 6.10547L8.44735 8.10547L7.55292 9.89433L3.55292 7.89433L4.44735 6.10547ZM15.5529 8.10547L19.5529 6.10547L20.4473 7.89433L16.4473 9.89433L15.5529 8.10547ZM11.0001 19.9999V16.9999H13.0001V19.9999H11.0001Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10.7758 10.7756L11.4309 8.81017H12.5693L13.2245 10.7756L15.1899 11.4307V12.5691L13.2245 13.2242L12.5693 15.1896H11.4309L10.7758 13.2242L8.8104 12.5691V11.4307L10.7758 10.7756Z"
        fill="currentColor"
      />
    </svg>
  ),
  collections: (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10.8763 3.06958C11.2448 3.02362 11.6199 3 12 3C12.3801 3 12.7552 3.02362 13.1237 3.06958L12.8763 5.05421C12.5896 5.01846 12.2972 5 12 5C11.7028 5 11.4104 5.01846 11.1237 5.05421L10.8763 3.06958ZM3 12C3 11.6199 3.02362 11.2448 3.06958 10.8763L5.05421 11.1237C5.01846 11.4104 5 11.7028 5 12C5 12.2972 5.01846 12.5896 5.05421 12.8763L3.06958 13.1237C3.02362 12.7552 3 12.3801 3 12ZM19 12C19 11.7028 18.9815 11.4104 18.9458 11.1237L20.9304 10.8763C20.9764 11.2448 21 11.6199 21 12C21 12.3801 20.9764 12.7552 20.9304 13.1237L18.9458 12.8763C18.9815 12.5896 19 12.2972 19 12ZM11.1237 18.9458C11.4104 18.9815 11.7028 19 12 19C12.2972 19 12.5896 18.9815 12.8763 18.9458L13.1237 20.9304C12.7552 20.9764 12.3801 21 12 21C11.6199 21 11.2448 20.9764 10.8763 20.9304L11.1237 18.9458Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M6.5 5C5.67157 5 5 5.67157 5 6.5C5 7.32843 5.67157 8 6.5 8C7.32843 8 8 7.32843 8 6.5C8 5.67157 7.32843 5 6.5 5ZM3 6.5C3 4.567 4.567 3 6.5 3C8.433 3 10 4.567 10 6.5C10 8.433 8.433 10 6.5 10C4.567 10 3 8.433 3 6.5Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M6.5 16C5.67157 16 5 16.6716 5 17.5C5 18.3284 5.67157 19 6.5 19C7.32843 19 8 18.3284 8 17.5C8 16.6716 7.32843 16 6.5 16ZM3 17.5C3 15.567 4.567 14 6.5 14C8.433 14 10 15.567 10 17.5C10 19.433 8.433 21 6.5 21C4.567 21 3 19.433 3 17.5Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M17.5 16C16.6716 16 16 16.6716 16 17.5C16 18.3284 16.6716 19 17.5 19C18.3284 19 19 18.3284 19 17.5C19 16.6716 18.3284 16 17.5 16ZM14 17.5C14 15.567 15.567 14 17.5 14C19.433 14 21 15.567 21 17.5C21 19.433 19.433 21 17.5 21C15.567 21 14 19.433 14 17.5Z"
        fill="currentColor"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M17.5 5C16.6716 5 16 5.67157 16 6.5C16 7.32843 16.6716 8 17.5 8C18.3284 8 19 7.32843 19 6.5C19 5.67157 18.3284 5 17.5 5ZM14 6.5C14 4.567 15.567 3 17.5 3C19.433 3 21 4.567 21 6.5C21 8.433 19.433 10 17.5 10C15.567 10 14 8.433 14 6.5Z"
        fill="currentColor"
      />
    </svg>
  ),
  trustAndSecurity: (
    <svg width="100%" height="100%" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.854 6.854C10.9479 6.76012 11.0006 6.63278 11.0006 6.5C11.0006 6.36723 10.9479 6.23989 10.854 6.146C10.7601 6.05212 10.6328 5.99937 10.5 5.99937C10.3672 5.99937 10.2399 6.05212 10.146 6.146L7.5 8.793L6.354 7.646C6.30751 7.59952 6.25232 7.56264 6.19158 7.53748C6.13084 7.51232 6.06574 7.49937 6 7.49937C5.93426 7.49937 5.86916 7.51232 5.80842 7.53748C5.74768 7.56264 5.69249 7.59952 5.646 7.646C5.59951 7.69249 5.56264 7.74768 5.53748 7.80842C5.51232 7.86916 5.49937 7.93426 5.49937 8C5.49937 8.06575 5.51232 8.13085 5.53748 8.19159C5.56264 8.25233 5.59951 8.30752 5.646 8.354L7.146 9.854C7.19245 9.90057 7.24762 9.93751 7.30837 9.96272C7.36911 9.98792 7.43423 10.0009 7.5 10.0009C7.56577 10.0009 7.63089 9.98792 7.69163 9.96272C7.75238 9.93751 7.80755 9.90057 7.854 9.854L10.854 6.854ZM8.354 2.147C8.26024 2.05327 8.13308 2.00061 8.0005 2.00061C7.86792 2.00061 7.74076 2.05327 7.647 2.147C6.404 3.388 5.03 4 3.5 4C3.36739 4 3.24021 4.05268 3.14645 4.14645C3.05268 4.24022 3 4.3674 3 4.5V7.501C3 10.72 4.641 12.908 7.842 13.974C7.94456 14.0082 8.05544 14.0082 8.158 13.974C11.358 12.908 13 10.72 13 7.501V4.5C13 4.3674 12.9473 4.24022 12.8536 4.14645C12.7598 4.05268 12.6326 4 12.5 4C10.969 4 9.594 3.39 8.354 2.147ZM4 4.98C5.48 4.868 6.814 4.254 8 3.188C9.185 4.254 10.52 4.868 12 4.98V7.5C12 8.934 11.637 10.065 10.979 10.944C10.341 11.796 9.37 12.487 8 12.971C6.63 12.488 5.659 11.796 5.021 10.944C4.363 10.065 4 8.935 4 7.501V4.98Z"
        fill="currentColor"
      />
    </svg>
  ),
  aiFirewall: (
    <svg width="100%" height="100%" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 1.75L13.25 3.75V8.75C13.25 11 11.25 13.25 8 14.25C4.75 13.25 2.75 11.25 2.75 8.75V3.75L8 1.75Z"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M5.75 7.75L7.25 9.25L10.25 5.75"
        stroke="currentColor"
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  ),
  rbac: (
    <svg
      width="100%"
      height="100%"
      stroke-width="2"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      color="currentColor"
    >
      <path d="M6 3H3V6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M18 3H21V6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path d="M6 21H3V18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
      <path
        d="M7 18V17C7 14.2386 9.23858 12 12 12V12C14.7614 12 17 14.2386 17 17V18"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path d="M18 21H21V18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
  ),
  manageCosts: (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 24 24"
      stroke-width="2"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      color="currentColor"
    >
      <path
        d="M9.01894 9C9.00639 8.83498 9 8.66824 9 8.5C9 4.91015 11.9101 2 15.5 2C19.0899 2 22 4.91015 22 8.5C22 12.0899 19.0899 15 15.5 15C15.3318 15 15.165 14.9936 15 14.9811"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M8.5 22C4.91015 22 2 19.0899 2 15.5C2 11.9101 4.91015 9 8.5 9C12.0899 9 15 11.9101 15 15.5C15 19.0899 12.0899 22 8.5 22Z"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M22 17C22 18.6569 20.6569 20 19 20H17M17 20L19 18M17 20L19 22"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M2 7C2 5.34315 3.34315 4 5 4H7M7 4L5 6M7 4L5 2"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  ),
  deSiloTeams: (
    <svg width="100%" height="100%" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12.048 14.169C10.79 14.9107 9.30464 15.1682 7.8704 14.8931C6.43616 14.618 5.15147 13.8293 4.25715 12.6748C3.36283 11.5203 2.92028 10.0793 3.01246 8.62179C3.10464 7.16432 3.72521 5.7905 4.75785 4.75785C5.7905 3.72521 7.16432 3.10464 8.62179 3.01246C10.0793 2.92028 11.5203 3.36283 12.6748 4.25715C13.8293 5.15147 14.618 6.43616 14.8931 7.8704C15.1682 9.30464 14.9107 10.79 14.169 12.048L20.952 18.828C21.8758 18.2847 22.9282 17.9988 24 18C25.11 18 26.154 18.303 27.048 18.831L33.828 12.048C33.0862 10.79 32.8287 9.30464 33.1038 7.8704C33.3789 6.43616 34.1676 5.15147 35.3221 4.25715C36.4766 3.36283 37.9177 2.92028 39.3751 3.01246C40.8326 3.10464 42.2064 3.72521 43.2391 4.75785C44.2717 5.7905 44.8923 7.16432 44.9845 8.62179C45.0766 10.0793 44.6341 11.5203 43.7398 12.6748C42.8454 13.8293 41.5608 14.618 40.1265 14.8931C38.6923 15.1682 37.2069 14.9107 35.949 14.169L29.169 20.952C29.7166 21.8743 30.0057 22.9272 30.0057 24C30.0057 25.0727 29.7166 26.1256 29.169 27.048L35.952 33.828C37.2099 33.0862 38.6953 32.8287 40.1295 33.1038C41.5638 33.3789 42.8484 34.1676 43.7428 35.3221C44.6371 36.4766 45.0796 37.9177 44.9875 39.3751C44.8953 40.8326 44.2747 42.2064 43.2421 43.2391C42.2094 44.2717 40.8356 44.8923 39.3781 44.9845C37.9207 45.0766 36.4796 44.6341 35.3251 43.7398C34.1706 42.8454 33.3819 41.5608 33.1068 40.1265C32.8317 38.6923 33.0892 37.2069 33.831 35.949L27.048 29.169C26.1244 29.7133 25.0719 30.0002 24 30C22.928 30.0002 21.8755 29.7133 20.952 29.169L14.172 35.952C14.9137 37.2099 15.1712 38.6953 14.8961 40.1295C14.621 41.5638 13.8323 42.8484 12.6778 43.7428C11.5233 44.6371 10.0823 45.0796 8.62479 44.9875C7.16732 44.8953 5.7935 44.2747 4.76085 43.2421C3.72821 42.2094 3.10764 40.8356 3.01546 39.3781C2.92328 37.9207 3.36583 36.4796 4.26015 35.3251C5.15447 34.1706 6.43916 33.3819 7.8734 33.1068C9.30764 32.8317 10.793 33.0892 12.051 33.831L18.831 27.048C18.2866 26.1244 17.9997 25.0719 18 24C18 22.89 18.303 21.846 18.831 20.952L12.048 14.169ZM12 8.99996C12 8.20431 11.6839 7.44124 11.1213 6.87864C10.5587 6.31603 9.79561 5.99996 8.99996 5.99996C8.20431 5.99996 7.44124 6.31603 6.87864 6.87864C6.31603 7.44124 5.99996 8.20431 5.99996 8.99996C5.99996 9.79561 6.31603 10.5587 6.87864 11.1213C7.44124 11.6839 8.20431 12 8.99996 12C9.79561 12 10.5587 11.6839 11.1213 11.1213C11.6839 10.5587 12 9.79561 12 8.99996ZM12 39C12 38.2043 11.6839 37.4412 11.1213 36.8786C10.5587 36.316 9.79561 36 8.99996 36C8.20431 36 7.44124 36.316 6.87864 36.8786C6.31603 37.4412 5.99996 38.2043 5.99996 39C5.99996 39.7956 6.31603 40.5587 6.87864 41.1213C7.44124 41.6839 8.20431 42 8.99996 42C9.79561 42 10.5587 41.6839 11.1213 41.1213C11.6839 40.5587 12 39.7956 12 39ZM39 12C39.7956 12 40.5587 11.6839 41.1213 11.1213C41.6839 10.5587 42 9.79561 42 8.99996C42 8.20431 41.6839 7.44124 41.1213 6.87864C40.5587 6.31603 39.7956 5.99996 39 5.99996C38.2043 5.99996 37.4412 6.31603 36.8786 6.87864C36.316 7.44124 36 8.20431 36 8.99996C36 9.79561 36.316 10.5587 36.8786 11.1213C37.4412 11.6839 38.2043 12 39 12ZM42 39C42 38.2043 41.6839 37.4412 41.1213 36.8786C40.5587 36.316 39.7956 36 39 36C38.2043 36 37.4412 36.316 36.8786 36.8786C36.316 37.4412 36 38.2043 36 39C36 39.7956 36.316 40.5587 36.8786 41.1213C37.4412 41.6839 38.2043 42 39 42C39.7956 42 40.5587 41.6839 41.1213 41.1213C41.6839 40.5587 42 39.7956 42 39ZM27 24C27 23.2043 26.6839 22.4412 26.1213 21.8786C25.5587 21.316 24.7956 21 24 21C23.2043 21 22.4412 21.316 21.8786 21.8786C21.316 22.4412 21 23.2043 21 24C21 24.7956 21.316 25.5587 21.8786 26.1213C22.4412 26.6839 23.2043 27 24 27C24.7956 27 25.5587 26.6839 26.1213 26.1213C26.6839 25.5587 27 24.7956 27 24Z"
        fill="currentColor"
      />
    </svg>
  ),
};
